import React, { useState } from 'react';
import { Card, Form, Button, Spinner, Alert, Table, Row, Col } from 'react-bootstrap';
import { FiCalendar, FiFileText, FiDownload } from 'react-icons/fi';
import {
    generateStandardEndOfStayTransferFiles,
    generateStandardEndOfStayTransferFilesByPolicyCodes,
} from '../../../services/api';
import { handleApiError } from '../../../utils/errorHandler';

export const parsePolicyCodes = (value) => Array.from(new Set(
    value.split(/[\s,;]+/).map((code) => code.trim()).filter(Boolean)
));

const formatReason = (reason) => {
    if (reason === null || reason === undefined) return '—';
    if (typeof reason === 'string') return reason;
    return JSON.stringify(reason);
};

export const ResultDetails = ({ data, renderFiles }) => {
    if (!data) return null;

    const files = Array.isArray(data.files) ? data.files : [];
    const transferDetails = data.transfer_details || {};
    const includedCodes = data.included_policy_codes || Object.keys(transferDetails);
    const excluded = data.excluded_policies || {};
    const diagnostics = data.diagnostics || {};
    const excludedEntries = Object.entries({ ...excluded, ...diagnostics });
    const hasFiles = files.length > 0;
    const errors = Array.isArray(data.errors) ? data.errors : [];

    return (
        <div className="mt-3">
            <Alert variant={hasFiles ? 'success' : 'info'} className="mb-3">
                <strong>{data.message || (hasFiles ? 'Fichiers générés avec succès.' : 'Aucun fichier généré.')}</strong>
                {data.date_range && (
                    <div className="small mt-1">
                        Fin de séjour dans ]{data.date_range.from}, {data.date_range.to}]
                    </div>
                )}
                {hasFiles && renderFiles(data)}
            </Alert>

            {data.hint && <Alert variant="warning" className="mb-3">{data.hint}</Alert>}

            {errors.length > 0 && (
                <Alert variant="danger" className="mb-3">
                    <strong>Erreurs :</strong>
                    <ul className="mb-0 mt-1">
                        {errors.map((err) => <li key={String(err)}>{formatReason(err)}</li>)}
                    </ul>
                </Alert>
            )}

            {data.diagnosis && (
                <Alert variant="warning" className="mb-3">
                    <strong>Diagnostic :</strong> {formatReason(data.diagnosis)}
                </Alert>
            )}

            {includedCodes.length > 0 && (
                <Card className="mb-3">
                    <Card.Header>
                        <strong>Polices incluses ({includedCodes.length})</strong>
                    </Card.Header>
                    <Table responsive size="sm" className="mb-0">
                        <thead>
                            <tr>
                                <th>Code police</th>
                                <th>Type de garantie</th>
                                <th className="text-end">Montant viré</th>
                            </tr>
                        </thead>
                        <tbody>
                            {includedCodes.map((code) => (
                                <tr key={code}>
                                    <td>{code}</td>
                                    <td>{transferDetails[code]?.coverage_type || '—'}</td>
                                    <td className="text-end">
                                        {transferDetails[code]?.amount ? `${transferDetails[code].amount} EUR` : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}

            {excludedEntries.length > 0 && (
                <Card className="mb-3">
                    <Card.Header>
                        <strong>Polices exclues ({excludedEntries.length})</strong>
                    </Card.Header>
                    <Table responsive size="sm" className="mb-0">
                        <thead>
                            <tr>
                                <th>Code police</th>
                                <th>Raison / diagnostic</th>
                            </tr>
                        </thead>
                        <tbody>
                            {excludedEntries.map(([code, reason]) => (
                                <tr key={code}>
                                    <td>{code}</td>
                                    <td className="text-break">{formatReason(reason)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
        </div>
    );
};

const SubmitButton = ({ loading, onClick, disabled }) => (
    <Button variant="primary" onClick={onClick} disabled={loading || disabled} className="mt-2">
        {loading ? (
            <>
                <Spinner size="sm" className="me-2" />
                Génération en cours...
            </>
        ) : (
            <>
                <FiDownload className="me-2" />
                Générer les fichiers
            </>
        )}
    </Button>
);

const useRequest = () => {
    const [state, setState] = useState({ loading: false, error: null, data: null });
    const run = async (fn, fallbackMessage) => {
        setState({ loading: true, error: null, data: null });
        try {
            const data = await fn();
            setState({ loading: false, error: null, data });
        } catch (err) {
            setState({ loading: false, error: handleApiError(err, fallbackMessage), data: null });
        }
    };
    return [state, run];
};

const StandardEndOfStayTransferPanel = ({ renderFiles }) => {
    const [method, setMethod] = useState('byDate');
    const [tripEndDateFrom, setTripEndDateFrom] = useState('');
    const [tripEndDateTo, setTripEndDateTo] = useState('');
    const [policyCode, setPolicyCode] = useState('');
    const [policyCodesInput, setPolicyCodesInput] = useState('');
    const [byDate, runByDate] = useRequest();
    const [byCodes, runByCodes] = useRequest();

    const policyCodes = parsePolicyCodes(policyCodesInput);
    const invalidRange = tripEndDateFrom && tripEndDateTo && tripEndDateFrom > tripEndDateTo;

    const handleGenerateByDate = () => runByDate(
        () => generateStandardEndOfStayTransferFiles({
            trip_end_date_from: tripEndDateFrom || undefined,
            trip_end_date_to: tripEndDateTo || undefined,
            policy_code: policyCode || undefined,
        }),
        'Erreur lors de la génération des fichiers end_of_stay Standard'
    );

    const handleGenerateByCodes = () => runByCodes(
        () => generateStandardEndOfStayTransferFilesByPolicyCodes(policyCodes),
        'Erreur lors de la génération des fichiers end_of_stay Standard par codes de police'
    );

    return (
        <>
            <p className="text-muted mb-3">
                Standard Partners — génère le CSV de virements et l&apos;Excel d&apos;import paid daily
                pour les sinistres <code>payment_schedule = end_of_stay</code>.
            </p>

            <Card className="mb-3">
                <Card.Header>
                    <h5 className="mb-0">Méthode de génération - End of Stay (Standard)</h5>
                </Card.Header>
                <Card.Body>
                    <Form.Check
                        type="radio"
                        className="mb-2"
                        label="Par date de fin de séjour"
                        name="standard-eos-method"
                        id="standard-eos-by-date"
                        checked={method === 'byDate'}
                        onChange={() => setMethod('byDate')}
                    />
                    <Form.Check
                        type="radio"
                        label="Par codes de police"
                        name="standard-eos-method"
                        id="standard-eos-by-codes"
                        checked={method === 'byPolicyCodes'}
                        onChange={() => setMethod('byPolicyCodes')}
                    />
                </Card.Body>
            </Card>

            {method === 'byDate' && (
                <Card className="mb-4">
                    <Card.Header>
                        <h5 className="mb-0">
                            <FiCalendar className="me-2" />
                            Génération par date de fin de séjour
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="standard-eos-from">
                                    <Form.Label>Fin de séjour (from, exclusive) - optionnel</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={tripEndDateFrom}
                                        onChange={(e) => setTripEndDateFrom(e.target.value)}
                                    />
                                    <Form.Text className="text-muted">Par défaut : 2026-07-01 (exclue).</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="standard-eos-to">
                                    <Form.Label>Fin de séjour (to, inclusive) - optionnel</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={tripEndDateTo}
                                        onChange={(e) => setTripEndDateTo(e.target.value)}
                                        isInvalid={Boolean(invalidRange)}
                                    />
                                    <Form.Text className="text-muted">Par défaut : hier (Paris).</Form.Text>
                                    <Form.Control.Feedback type="invalid">
                                        La date « to » doit être postérieure à la date « from ».
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3" controlId="standard-eos-policy-code">
                            <Form.Label>Code police - optionnel</Form.Label>
                            <Form.Control
                                type="text"
                                value={policyCode}
                                onChange={(e) => setPolicyCode(e.target.value)}
                                placeholder="Ex. 658204658"
                            />
                            <Form.Text className="text-muted">
                                Limite la génération à une police ; un diagnostic est renvoyé si elle est absente du fichier.
                            </Form.Text>
                        </Form.Group>

                        <Alert variant="light" className="border small mb-2">
                            Un e-mail est envoyé aux administrateurs avec les liens S3 lorsque des fichiers sont générés.
                        </Alert>

                        <SubmitButton
                            loading={byDate.loading}
                            onClick={handleGenerateByDate}
                            disabled={Boolean(invalidRange)}
                        />

                        {byDate.error && <Alert variant="danger" className="mt-3">{byDate.error}</Alert>}
                        <ResultDetails data={byDate.data} renderFiles={renderFiles} />
                    </Card.Body>
                </Card>
            )}

            {method === 'byPolicyCodes' && (
                <Card className="mb-4">
                    <Card.Header>
                        <h5 className="mb-0">
                            <FiFileText className="me-2" />
                            Génération par codes de police
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3" controlId="standard-eos-policy-codes">
                            <Form.Label>Codes de police (requis)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={policyCodesInput}
                                onChange={(e) => setPolicyCodesInput(e.target.value)}
                                placeholder="Ex. 658204658, 123456789"
                            />
                            <Form.Text className="text-muted">
                                Séparez les codes par des virgules, espaces ou retours à la ligne.
                                {policyCodes.length > 0 && ` ${policyCodes.length} code(s) détecté(s).`}
                                {' '}Sans filtre de dates ; les polices au montant nul sont exclues ; pas d&apos;e-mail admin.
                            </Form.Text>
                        </Form.Group>

                        <SubmitButton
                            loading={byCodes.loading}
                            onClick={handleGenerateByCodes}
                            disabled={policyCodes.length === 0}
                        />

                        {byCodes.error && <Alert variant="danger" className="mt-3">{byCodes.error}</Alert>}
                        <ResultDetails data={byCodes.data} renderFiles={renderFiles} />
                    </Card.Body>
                </Card>
            )}
        </>
    );
};

export default StandardEndOfStayTransferPanel;
